import { isKeyOf } from "@p0security/shared/types/is";
import { integrationLabels } from "@p0security/shared/types/workflow/constants";
import type { SecretManagement } from "@p0security/shared/vault/types";
import {
  Button,
  Descriptions,
  Drawer,
  Flex,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { format, formatDistance, parseISO } from "date-fns";
import { get } from "lodash";
import {
  ChangeEventHandler,
  type MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { colors } from "styles/variables";

import { StyledTag } from "../../styles/StyledTags.tsx";
import { ErrorDisplay } from "../Error.tsx";
import { Heading } from "../Heading/index.ts";
import {
  ResizableTable,
  ResizableTableColumnType,
} from "../ResizableTable.tsx";
import { SearchInput } from "../antd.tsx";
import { SecretEditor } from "./SecretEditor.tsx";
import { VaultData, useVault } from "./useVault.ts";

const secretPath = (args: { name?: string; filter?: string }) => ({
  pathname: args.name,
  search: new URLSearchParams(
    args.filter ? [["filter", args.filter]] : []
  ).toString(),
});

const age = (value: string) => formatDistance(parseISO(value), Date.now());
const rotatedOn = (value: string) => format(parseISO(value), "PPP");

const ManagementDescriptions: Record<SecretManagement, string> = {
  admin: "Used by P0 to connect to a resource",
  cico: "Credential is rotated after use",
  ephemeral: "Credential is removed after use",
  manual: "Manually managed by your organization",
  scheduled: "Credential is rotated on a fixed schedule",
};

const VaultTags: Record<SecretManagement, ReactNode> = {
  admin: <StyledTag color={colors.tagColors.lightGrey}>P0 admin</StyledTag>,
  cico: <StyledTag color={colors.tagColors.orange}>After use</StyledTag>,
  ephemeral: <StyledTag color={colors.tagColors.grey}>Ephemeral</StyledTag>,
  manual: <StyledTag color={colors.tagColors.yellow}>Manual</StyledTag>,
  scheduled: <StyledTag color={colors.tagColors.blue}>Scheduled</StyledTag>,
};
const ManualTag = (
  <Tooltip title={ManagementDescriptions.manual}>{VaultTags.manual}</Tooltip>
);

const VaultColumns = (
  projectId: string,
  filterRef: MutableRefObject<string | undefined>
): ResizableTableColumnType<VaultData>[] => [
  {
    key: "name",
    title: "Name",
    render: (value) => value.name,
    sorter: (a, b) => a.name.localeCompare(b.name),
    width: 300,
  },
  {
    key: "createdOn",
    title: "Age",
    render: (value) =>
      typeof value.createdOn === "string" ? (
        <Tooltip title={rotatedOn(value.createdOn)}>
          {age(value.createdOn)}
        </Tooltip>
      ) : (
        "(no value)"
      ),
    sorter: (a, b) =>
      a.createdOn && b.createdOn
        ? a.createdOn.localeCompare(b.createdOn)
        : a.createdOn
          ? -1
          : 1,
    width: 100,
  },
  {
    key: "management",
    title: "Management",
    render: (value) => {
      const tag = get(VaultTags, value.management);
      return tag ? (
        <Tooltip title={get(ManagementDescriptions, value.management)}>
          {tag}
        </Tooltip>
      ) : (
        ManualTag
      );
    },
    sorter: (a, b) =>
      (a.management ?? "manual").localeCompare(b.management ?? "manual"),
    width: 100,
  },
  {
    key: "account",
    title: "Account",
    render: (value) => (
      <>
        {value.account && (
          <StyledTag color={colors.tagColors.blue}>{value.account}</StyledTag>
        )}
        {value.location && (
          <>
            {value.account && <> in </>}
            <StyledTag color={colors.tagColors.lightGrey}>
              {value.location}
            </StyledTag>
          </>
        )}
      </>
    ),
    width: 200,
  },
  {
    key: "actions",
    title: "Actions",
    render: (value) => (
      <Link
        to={secretPath({ name: value.name, filter: filterRef.current })}
        relative="path"
      >
        view
      </Link>
    ),
    width: 100,
  },
];

const VaultTable: React.FC<{
  filter?: string;
  projectId: string;
  vault: VaultData[];
}> = ({ filter, projectId, vault }) => {
  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  const columns = useMemo(
    () => VaultColumns(projectId, filterRef),
    [projectId]
  );
  const data = useMemo(
    () =>
      filter
        ? vault.filter((v) =>
            Object.values(v).some(
              (vv) =>
                typeof vv === "string" &&
                vv.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            )
          )
        : vault,
    [filter, vault]
  );
  return (
    <ResizableTable<VaultData>
      columns={columns}
      dataSource={data}
      tableId="vault"
    />
  );
};

const VaultDrawer: React.FC<{ projectId: string; secret: VaultData }> = ({
  projectId,
  secret,
}) => (
  <Descriptions
    bordered
    column={1}
    styles={{ label: { color: "darkgray" } }}
    layout="horizontal"
  >
    <Descriptions.Item label="Name">{secret.name}</Descriptions.Item>
    <Descriptions.Item label="Last rotated">
      {secret.createdOn ? (
        <>
          {rotatedOn(secret.createdOn)}{" "}
          <Typography.Text type="secondary">
            ({age(secret.createdOn)} ago)
          </Typography.Text>
        </>
      ) : (
        <Typography.Text type="secondary">(no value)</Typography.Text>
      )}
    </Descriptions.Item>
    <Descriptions.Item label="Management">
      {ManagementDescriptions[secret.management ?? "manual"]}
    </Descriptions.Item>
    <Descriptions.Item label="Resource">
      {secret.resource && isKeyOf(integrationLabels, secret.resource) ? (
        integrationLabels[secret.resource]
      ) : (
        <Typography.Text>(unknown)</Typography.Text>
      )}
    </Descriptions.Item>
    <Descriptions.Item label="Location">
      {secret.location ?? <Typography.Text>(unknown)</Typography.Text>}
    </Descriptions.Item>
    <Descriptions.Item label="Account">
      {secret.account ?? <Typography.Text>(unknown)</Typography.Text>}
    </Descriptions.Item>
    {secret.request && (
      <Descriptions.Item label="Request">
        <Link
          to={`../../access-management/history/${secret.request}`}
          rel="route"
        >
          Request {secret.request}
        </Link>
      </Descriptions.Item>
    )}
    <Descriptions.Item label="Cloud secret">
      <Button
        href={`https://console.cloud.google.com/security/secret-manager/secret/${secret.name}/versions?project=${projectId}`}
        target="secret-manager"
        rel="noopener"
        type="link"
      >
        View in Google Secret Manager
      </Button>{" "}
      <Typography.Text type="secondary">
        (requires <Typography.Text code>secretmanager.viewer</Typography.Text>{" "}
        role in GCP)
      </Typography.Text>
    </Descriptions.Item>
  </Descriptions>
);

export const Vault: React.FC<object> = () => {
  const navigate = useNavigate();
  const { error, vault, vaultLoading } = useVault();
  const [search, setSearch] = useSearchParams();
  const { id } = useParams<{ id: string }>();

  const filter = useMemo(() => search.get("filter") ?? undefined, [search]);
  const setFilter = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) =>
      setSearch((next) => {
        const filter = event.target.value;
        if (filter) {
          next.set("filter", filter);
        } else {
          next.delete("filter");
        }
        return next;
      }),
    [setSearch]
  );

  const secret = useMemo(
    () => vault?.vault.find((v) => v.name === id),
    [id, vault?.vault]
  );

  const closeDrawer = useCallback(
    () => navigate(secretPath({ filter, name: ".." }), { relative: "path" }),
    [filter, navigate]
  );

  return (
    <>
      <Heading title="Vault" />
      {error && <ErrorDisplay error={error} />}
      {vaultLoading || !vault ? (
        <Spin />
      ) : (
        <Flex vertical gap="10px" style={{ maxWidth: 800 }}>
          <SearchInput
            placeholder="Filter credentials"
            value={filter}
            onChange={setFilter}
          />
          <VaultTable
            filter={filter}
            projectId={vault.projectId}
            vault={vault.vault}
          />
          <Drawer
            open={!!secret}
            onClose={closeDrawer}
            styles={{
              wrapper: { minWidth: "400", width: "60%", maxWidth: "800" },
            }}
          >
            {secret && (
              <>
                <VaultDrawer projectId={vault.projectId} secret={secret} />
                <SecretEditor
                  secretName={secret.name}
                  projectId={vault.projectId}
                />
              </>
            )}
          </Drawer>
        </Flex>
      )}
    </>
  );
};
